import type {
    Manifest,
    UI5Version,
    ChangeDefinition,
    ChangeDefinitionInCreation,
    SyncRule
} from '@sap/ux-specification-types';
import * as flexUtils from '@ui5/flexibility-utils';
import { FlexChangeLayer } from '@sap/ux-specification-types';

const changesUtils = flexUtils.default.change;

const defaultValues = {
    isCustomer: false,
    sapui5Version: ' ', // UI5 version should be set via exportConfig(). This is the fallback in case it's missing
    creatingTool: 'SAPFioriTools',
    type: 'propertyChange'
};

/**
 * Creates a serialized string representation of a flex change using ui5-flexibility-utils.
 *
 * @param change - Properties required to create the change.
 * @param manifest - The manifest.json file.
 * @returns {string} - The serialized change string.
 */
export function createChangeString(change: ChangeDefinitionInCreation, manifest: Manifest): string {
    let changeString = '';
    try {
        for (const defaultValue in defaultValues) {
            if (change[defaultValue] === undefined) {
                change[defaultValue] = defaultValues[defaultValue];
            }
        }
        changeString = changesUtils.createChangeString(change, manifest);
    } catch (error) {
        console.error(`Error while create flex change.`, error);
    }
    return changeString;
}

/**
 * Parse string and return the change as object (calls changeUtils from ui5-flexibility-utils).
 *
 * @param changeString - string with serialized change
 * @returns {ChangeDefinition | boolean} - The parsed change object or false if parsing fails.
 */
export function parseChangeString(changeString: string): ChangeDefinition | boolean {
    try {
        return changesUtils.parse(changeString);
    } catch {
        return false;
    }
}

/**
 * Creates a flex change based on the given config information.
 *
 * @param configObject - current (sub)object of the configuration file
 * @param syncRule - export rule from the object classes decorator
 * @param ui5Version - SAP UI5 version
 * @param key - key of the given property
 * @param controlId - The ID of the control for which the flex change is being created.
 * @returns {ChangeDefinitionInCreation} - the newly created flex change
 */
export function fillFlexChangeContent(
    configObject: object,
    syncRule: SyncRule,
    ui5Version: UI5Version,
    key: string,
    controlId: string
): ChangeDefinitionInCreation {
    const isCustomer = ui5Version.layer ? ui5Version.layer === FlexChangeLayer.Customer : true;

    const flexChange: ChangeDefinitionInCreation = {
        controlId, //syncRule.flex.controlId(baseId, ids, breadcrumbs, syncRule.flex.controlType, title),
        controlType: syncRule.flex.controlType(),
        content: {
            property: key
        },
        sapui5Version: ui5Version.ui5Version,
        isCustomer
    };
    if (configObject[key] !== undefined) {
        if (syncRule.flex.exportFlexValue) {
            syncRule.flex.exportFlexValue(flexChange, configObject, key);
        } else if (configObject[key].toString().startsWith('{')) {
            flexChange.content.newBinding = configObject[key];
            flexChange.type = 'propertyBindingChange';
        } else {
            flexChange.content.newValue = configObject[key];
        }
    } else {
        flexChange.content.newValue = null;
    }
    return flexChange;
}
